import type {
  FieldItemEvent,
  FieldMissionHeader,
  FieldMissionItem,
  FieldStartBlocker,
  GpsGapKind,
  HelpRequestReason,
  HelpRequestStatus,
  HourReading,
  OperatorMissionHome,
  OperatorReinforcementOffer,
  OperatorRoutePlan,
  RouteAction,
} from '@groupe-reca/contracts'
import { fromLocal } from '@/features/detection/domain/geometry'
import { demoProfile } from '@/features/detection/domain/profile'
import { ServerCommandError, UncertainResultError } from '@/infrastructure/supabase/client'
import type { MissionRepository } from '../../application/MissionRepository'
import { forceGuideStatus, fixtureRouteGuide } from './fixtureRouteGuide'

// Serveur simulé du mode fixtures (développement et tests) : mêmes règles que les migrations 20261022010000 et
// 20261101010000 à 20261101030000 (idempotence par clé, une sortie ouverte par opérateur / tracteur / route, ordre
// figé, compteur jamais inférieur, aucun travail pendant une pause, positions dédupliquées; opération ouverte unique,
// annulation d'un démarrage sans travail, présence, aide, renfort avec un seul responsable) et 20261115020000 (routes
// ordonnées par opérateur, passages préparés par le bureau, passage décidé par le serveur au départ). Données de
// démonstration, jamais réelles. Le bureau et les autres tablettes se simulent par `window.__recaFixtureServer`.
// État conservé dans le navigateur pour qu'un rechargement de page reprenne la même mission.

const storageKey = 'reca-operator-v3-fixture-server'

/** Profil figé au démarrage, comme le fait le serveur (Démo v0.3, observation : rien n'est appliqué). */
const fixtureDetectionProfile = (() => {
  const { id, version, label, mode, certified, ...parameters } = demoProfile
  return { id: 'd5000000-0000-4000-a000-000000000003', version, label: `${label} (${id})`, mode, certified, parameters }
})()

/** Drapeau fictif : 6 m au sud du point d'intervention (côté rue); aucun sans coordonnées. */
function fixtureFlag(lat: number | null, lng: number | null) {
  if (lat === null || lng === null) return null
  const flag = fromLocal({ lat, lng }, { x: 0, y: -6 })
  return { latitude: flag.lat, longitude: flag.lng, source: 'GEOMETRY', version: 1 }
}

interface Operator {
  employeeId: string
  name: string
  /** Route principale (rang 1), seule lue par l'accueil historique. */
  routeId: string | null
  /** Routes dans l'ordre du bureau; la première est `routeId`. */
  routeIds: string[]
  equipmentId: string | null
}

interface StoredMission {
  header: Omit<FieldMissionHeader, 'progress' | 'pausedSeconds'>
  items: FieldMissionItem[]
  pauses: { startedAt: string; endedAt: string | null }[]
  /** Passage engagé par ce départ (absent dans un état antérieur : passage 1). */
  passNumber?: number
}

interface FixturePass {
  id: string
  operationId: string
  routeId: string
  passNumber: number
  status: 'PREPARED' | 'STARTED' | 'CANCELLED'
}

interface FixtureOperation {
  id: string
  number: string
  status: 'OPEN' | 'CLOSED' | 'CANCELLED'
  openedAt: string
  openedByMissionId: string
}

interface FixtureOffer {
  offerId: string
  /** Résidence de la mission responsable au moment de l'offre. */
  itemId: string
  taskId: string
  taskVersion: number
  operationId: string
  status: 'ACTIVE' | 'CLAIMED' | 'REVOKED'
  createdAt: string
  claimedAt: string | null
  claimedByName: string | null
  claimedByEmployeeId: string | null
}

interface FixtureHelpRequest {
  id: string
  employeeId: string
  userId: string
  reason: HelpRequestReason
  comment: string | null
  status: HelpRequestStatus
  createdAt: string
  acknowledgedAt: string | null
  acknowledgedByName: string | null
  resolvedAt: string | null
}

interface ServerState {
  missions: StoredMission[]
  operations: FixtureOperation[]
  offers: FixtureOffer[]
  presence: Record<string, { sessionId: string; connected: boolean; lastBeatAt: string; availableForHelp: boolean }>
  help: FixtureHelpRequest[]
  passes: FixturePass[]
  readings: Record<string, HourReading[]>
  commands: Record<string, { userId: string; command: string; target: string | null; result: unknown }>
  positions: Record<string, { missionId: string; capturedAt: string; latitude: number; longitude: number; accuracy: number | null; receivedAt: string }>
  gaps: { id: string; missionId: string; kind: GpsGapKind; startedAt: string; endedAt: string | null }[]
  sequence: number
}

const operators: Record<string, Operator> = {
  'a0000000-0000-4000-a000-000000000001': {
    employeeId: 'b0000000-0000-4000-a000-000000000001',
    name: 'Olivier Gauthier',
    routeId: 'c0000000-0000-4000-a000-000000000001',
    routeIds: ['c0000000-0000-4000-a000-000000000001', 'c0000000-0000-4000-a000-000000000002'],
    equipmentId: 'd0000000-0000-4000-a000-000000000004',
  },
  'a0000000-0000-4000-a000-000000000002': {
    employeeId: 'b0000000-0000-4000-a000-000000000002',
    name: 'Maude Pelletier',
    routeId: 'c0000000-0000-4000-a000-000000000002',
    routeIds: ['c0000000-0000-4000-a000-000000000002'],
    equipmentId: null,
  },
  'a0000000-0000-4000-a000-000000000003': {
    employeeId: 'b0000000-0000-4000-a000-000000000003',
    name: 'Noé Temporaire',
    routeId: 'c0000000-0000-4000-a000-000000000002',
    routeIds: ['c0000000-0000-4000-a000-000000000002'],
    equipmentId: 'd0000000-0000-4000-a000-000000000002',
  },
}

const routes: Record<string, { name: string; color: string; version: number }> = {
  'c0000000-0000-4000-a000-000000000001': { name: 'Route Bleu', color: '#3B82F6', version: 7 },
  'c0000000-0000-4000-a000-000000000002': { name: 'Route Vert', color: '#22C55E', version: 3 },
}

/** Route suivante de l'opérateur (ordre du bureau) et sa première résidence localisée. */
function nextRouteOf(operator: Operator, routeId: string) {
  const nextId = operator.routeIds[operator.routeIds.indexOf(routeId) + 1]
  const route = nextId ? routes[nextId] : undefined
  if (!nextId || !route) return null
  const first = residences.find((residence) => residence.lat !== null)
  return {
    routeId: nextId,
    name: route.name,
    color: route.color,
    firstStop: first ? ([first.lat, first.lng] as [number, number]) : null,
  }
}

const equipments: Record<string, { number: string; name: string }> = {
  'd0000000-0000-4000-a000-000000000004': { number: 'T-04', name: 'Kubota M7060' },
  'd0000000-0000-4000-a000-000000000002': { number: 'T-02', name: 'John Deere 5075E' },
}

const residences = [
  { line1: '120 rue Labelle', unit: null, client: 'Claire Bélanger', phone: '450 555-0101', lat: 45.7783, lng: -74.0035 },
  { line1: '134 rue Labelle', unit: null, client: 'Jacques Gagnon', phone: '450 555-0102', lat: 45.7787, lng: -74.0041 },
  { line1: '148 rue Saint-Georges', unit: 'Unité 3', client: 'Famille Roy', phone: null, lat: 45.7792, lng: -74.0049 },
  { line1: '162 rue Saint-Georges', unit: null, client: 'Nadia Tremblay', phone: '450-555-0104', lat: null, lng: null },
  { line1: '176 boulevard des Hauteurs', unit: null, client: 'Pierre Lavoie', phone: '4505550105', lat: 45.7801, lng: -74.0061 },
  { line1: '190 boulevard des Hauteurs', unit: null, client: 'Sophie Côté', phone: '450 555-0106', lat: 45.7806, lng: -74.0068 },
] as const

function initialState(): ServerState {
  return {
    missions: [],
    operations: [],
    offers: [],
    presence: {},
    help: [],
    passes: [],
    readings: {
      'd0000000-0000-4000-a000-000000000004': [
        { value: 1520.5, kind: 'END', recordedAt: '2026-10-09T21:40:00.000Z', missionId: null },
      ],
    },
    commands: {},
    positions: {},
    gaps: [],
    sequence: 40,
  }
}

function load(): ServerState {
  try {
    const raw = window.localStorage.getItem(storageKey)
    // État d'une version antérieure du serveur simulé : complété, jamais perdu.
    return raw ? { ...initialState(), ...(JSON.parse(raw) as Partial<ServerState>) } : initialState()
  } catch {
    return initialState()
  }
}

function save(state: ServerState) {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(state))
  } catch {
    // stockage indisponible : l'état ne survit pas au rechargement
  }
}

function now(): string {
  return new Date().toISOString()
}

/** Les résidences prises en charge par une autre mission sortent des restantes (`transferred`). */
function progress(items: FieldMissionItem[]) {
  const own = items.filter((item) => !item.transfer)
  return {
    total: own.length,
    completed: own.filter((item) => item.status === 'COMPLETED').length,
    problem: own.filter((item) => item.status === 'PROBLEM').length,
    waiting: own.filter((item) => item.status === 'WAITING').length,
    transferred: items.length - own.length,
  }
}

function header(mission: StoredMission): FieldMissionHeader {
  const pausedSeconds = mission.pauses.reduce(
    (total, pause) =>
      total + Math.max(0, ((pause.endedAt ? Date.parse(pause.endedAt) : Date.now()) - Date.parse(pause.startedAt)) / 1000),
    0,
  )
  return { ...mission.header, progress: progress(mission.items), pausedSeconds: Math.round(pausedSeconds) }
}

function fail(code: string, detail?: string): never {
  throw new ServerCommandError(code, detail)
}

/** Seconde tablette simulée : sa mission sur la Route Vert fournit les résidences offertes en renfort. */
const otherOperator = { employeeId: 'b0000000-0000-4000-a000-000000000009', name: 'Julien Morin' }
const otherResidences = [
  { line1: '15 rue des Érables', lat: 45.7771, lng: -74.0012 },
  { line1: '27 rue des Érables', lat: 45.7768, lng: -74.0006 },
  { line1: '39 rue des Érables', lat: 45.7765, lng: -74.0 },
] as const

export interface FixtureControls {
  /** Le prochain appel est appliqué par le « serveur » mais sa réponse est perdue (résultat incertain). */
  dropNextResponse: () => void
  reset: () => void
  /**
   * Bureau : ouvre (au besoin) la mission d'une seconde tablette dans l'opération ouverte et offre ses résidences
   * à faire à tous les opérateurs admissibles. Retourne le nombre d'offres publiées.
   */
  publishOffers: (count?: number) => number
  /** Une autre tablette prend l'offre avant nous (« Déjà prise en charge par … »). */
  claimOfferElsewhere: (address: string, operatorName: string) => void
  /** Bureau : « Je m'en occupe » sur les demandes d'aide envoyées. */
  acknowledgeHelp: (byName: string) => void
  /**
   * Bureau : prépare un nouveau passage sur les routes déjà engagées, sans mission ouverte ni restantes, de
   * l'opération ouverte. Retourne le nombre de passages préparés.
   */
  preparePasses: () => number
  /** Statut du parcours figé aux prochains départs (`null` : Route Bleu validée, Route Vert sans parcours). */
  setRouteGuide: (status: 'READY' | 'NOT_CALCULATED' | 'STALE' | 'EXPIRED' | null) => void
}

declare global {
  interface Window {
    __recaFixtureServer?: FixtureControls
  }
}

export function createFixtureMissionRepository(currentUserId: () => string | null): MissionRepository {
  let dropNext = false
  if (typeof window !== 'undefined') {
    window.__recaFixtureServer = {
      dropNextResponse: () => {
        dropNext = true
      },
      reset: () => {
        save(initialState())
      },
      setRouteGuide: (status) => {
        forceGuideStatus(status)
      },
      preparePasses: () => {
        const state = load()
        const operation = openOperation(state)
        if (!operation) return 0
        let prepared = 0
        for (const routeId of Object.keys(routes)) {
          const current = routeState(state, operation.id, routeId)
          if (current.action !== 'COMPLETED' || current.currentPass === null) continue
          state.passes.push({
            id: crypto.randomUUID(),
            operationId: operation.id,
            routeId,
            passNumber: current.currentPass + 1,
            status: 'PREPARED',
          })
          prepared += 1
        }
        save(state)
        return prepared
      },
      publishOffers: (count = 2) => {
        const state = load()
        const operation = openOperation(state)
        if (!operation) return 0
        let other = openMissionOf(state, otherOperator.employeeId)
        if (!other) {
          const missionId = crypto.randomUUID()
          state.sequence += 1
          const created: StoredMission = {
            header: {
              id: missionId,
              number: state.sequence,
              status: 'IN_PROGRESS',
              legacyStatus: 'en_cours',
              organizationId: null,
              season: '2026-2027',
              routeId: 'c0000000-0000-4000-a000-000000000002',
              routeName: 'Route Vert',
              routeColor: '#22C55E',
              routeVersion: 3,
              routeDescription: null,
              operatorId: otherOperator.employeeId,
              operatorName: otherOperator.name,
              equipmentId: 'd0000000-0000-4000-a000-000000000009',
              equipmentLabel: 'T-09 — New Holland T5',
              startedAt: now(),
              endedAt: null,
              pausedAt: null,
              startEngineHours: 980,
              endEngineHours: null,
              endSummary: null,
              cancelledReason: null,
              operationId: operation.id,
              operationNumber: operation.number,
              missionKind: 'NORMAL',
              cancelKind: null,
            },
            items: otherResidences.map((residence, index) => ({
              id: crypto.randomUUID(),
              operationTaskId: crypto.randomUUID(),
              transfer: null,
              missionId,
              order: index + 1,
              status: 'WAITING' as const,
              itemVersion: 1,
              serviceLocationId: `e0000000-0000-4000-a000-${String(index + 101).padStart(12, '0')}`,
              contractId: `f0000000-0000-4000-a000-${String(index + 101).padStart(12, '0')}`,
              snapshot: {
                addressLine1: residence.line1,
                addressLine2: null,
                city: 'Saint-Jérôme',
                postalCode: 'J7Z 2B2',
                formattedAddress: null,
                clientName: null,
                contractNumber: null,
                latitude: residence.lat,
                longitude: residence.lng,
                routeOrder: index + 1,
                flag: fixtureFlag(residence.lat, residence.lng),
              },
              completedAt: null,
              clearedAt: null,
              clearedReason: null,
              events: [],
              phone: null,
            })),
            pauses: [],
          }
          state.missions.push(created)
          other = created
        }
        let published = 0
        for (const item of other.items) {
          if (published >= count) break
          if (item.status !== 'WAITING' || item.transfer) continue
          if (state.offers.some((offer) => offer.itemId === item.id && offer.status !== 'REVOKED')) continue
          state.offers.push({
            offerId: crypto.randomUUID(),
            itemId: item.id,
            taskId: item.operationTaskId ?? crypto.randomUUID(),
            taskVersion: 1,
            operationId: operation.id,
            status: 'ACTIVE',
            createdAt: now(),
            claimedAt: null,
            claimedByName: null,
            claimedByEmployeeId: null,
          })
          published += 1
        }
        save(state)
        return published
      },
      claimOfferElsewhere: (address, operatorName) => {
        const state = load()
        const offer = state.offers.find(
          (candidate) => candidate.status === 'ACTIVE' && ownerOf(state, candidate.itemId)?.item.snapshot.addressLine1 === address,
        )
        if (!offer) return
        const origin = ownerOf(state, offer.itemId)
        if (origin) origin.item.transfer = { at: now(), toMissionId: crypto.randomUUID(), operatorName }
        offer.status = 'CLAIMED'
        offer.claimedAt = now()
        offer.claimedByName = operatorName
        offer.claimedByEmployeeId = null
        offer.taskVersion += 1
        save(state)
      },
      acknowledgeHelp: (byName) => {
        const state = load()
        for (const request of state.help) {
          if (request.status !== 'SENT') continue
          request.status = 'ACKNOWLEDGED'
          request.acknowledgedAt = now()
          request.acknowledgedByName = byName
        }
        save(state)
      },
    }
  }

  function network() {
    if (typeof navigator !== 'undefined' && !navigator.onLine) throw new UncertainResultError()
  }

  function actor(): { userId: string; operator: Operator } {
    const userId = currentUserId()
    const operator = userId ? operators[userId] : undefined
    if (!userId || !operator) fail('MISSION_OPERATOR_ROLE_REQUIRED')
    return { userId, operator }
  }

  async function run<T>(fn: (state: ServerState) => T): Promise<T> {
    await new Promise((resolve) => setTimeout(resolve, 120))
    network()
    const state = load()
    const result = fn(state)
    save(state)
    if (dropNext) {
      dropNext = false
      throw new UncertainResultError()
    }
    return result
  }

  function claim<T>(
    state: ServerState,
    userId: string,
    key: string,
    command: string,
    target: string | null,
    apply: () => T,
  ): T & { replayed: boolean } {
    const existing = state.commands[key]
    if (existing) {
      if (existing.userId !== userId || existing.command !== command || (target && existing.target !== target))
        fail('MISSION_IDEMPOTENCY_KEY_REUSED')
      return { ...(existing.result as T), replayed: true }
    }
    const result = apply()
    state.commands[key] = { userId, command, target, result }
    return { ...result, replayed: false }
  }

  const open = (mission: StoredMission) =>
    mission.header.status === 'IN_PROGRESS' || mission.header.status === 'PAUSED'
  /** Une mission de renfort n'occupe pas la route de l'opérateur. */
  const occupiesRoute = (mission: StoredMission) => open(mission) && mission.header.missionKind !== 'REINFORCEMENT'

  function openOperation(state: ServerState): FixtureOperation | null {
    return state.operations.find((operation) => operation.status === 'OPEN') ?? null
  }

  /** Départ : rejoint l'opération ouverte de la saison, sinon en ouvre une (jamais une seconde). */
  function attachOperation(state: ServerState, missionId: string): FixtureOperation {
    const existing = openOperation(state)
    if (existing) return existing
    const operation: FixtureOperation = {
      id: crypto.randomUUID(),
      number: `2026-${String(state.operations.length + 14).padStart(3, '0')}`,
      status: 'OPEN',
      openedAt: now(),
      openedByMissionId: missionId,
    }
    state.operations.push(operation)
    return operation
  }

  function openMissionOf(state: ServerState, employeeId: string): StoredMission | undefined {
    return state.missions.find((mission) => mission.header.operatorId === employeeId && open(mission))
  }

  /**
   * État d'une route dans l'opération, du point de vue d'un départ (`operation_route_state_v1`) : mission normale
   * ouverte, passage préparé, passage en cours et résidences réglées dans ce passage.
   */
  function routeState(state: ServerState, operationId: string | null, routeId: string) {
    const total = residences.length
    if (!operationId)
      return { action: 'START' as RouteAction, passNumber: 1, currentPass: null, preparedPassId: null, total: 0, done: 0, remaining: 0, inUseBy: null }
    const missions = state.missions.filter(
      (mission) =>
        mission.header.operationId === operationId &&
        mission.header.routeId === routeId &&
        mission.header.missionKind !== 'REINFORCEMENT' &&
        mission.header.status !== 'CANCELLED',
    )
    const inUse = missions.find(open)
    const prepared = state.passes.find(
      (pass) => pass.operationId === operationId && pass.routeId === routeId && pass.status === 'PREPARED',
    )
    const currentPass = missions.length > 0 ? Math.max(...missions.map((mission) => mission.passNumber ?? 1)) : null
    const done = new Set(
      missions
        .filter((mission) => (mission.passNumber ?? 1) === currentPass)
        .flatMap((mission) => mission.items)
        .filter((item) => item.status === 'COMPLETED')
        .map((item) => item.serviceLocationId),
    ).size
    const counts = currentPass === null ? { total: 0, done: 0, remaining: 0 } : { total, done, remaining: total - done }
    const action: RouteAction = inUse
      ? 'IN_USE'
      : prepared
        ? 'NEW_PASS'
        : currentPass === null
          ? 'START'
          : counts.remaining > 0
            ? 'RESUME'
            : 'COMPLETED'
    return {
      action,
      passNumber: prepared ? prepared.passNumber : (currentPass ?? 1),
      currentPass,
      preparedPassId: prepared?.id ?? null,
      ...counts,
      inUseBy: inUse ? inUse.header.operatorName : null,
    }
  }

  function ownerOf(state: ServerState, itemId: string): { mission: StoredMission; item: FieldMissionItem } | null {
    for (const mission of state.missions) {
      const item = mission.items.find((candidate) => candidate.id === itemId)
      if (item) return { mission, item }
    }
    return null
  }

  function offerView(state: ServerState, offer: FixtureOffer, operator: Operator): OperatorReinforcementOffer | null {
    const origin = ownerOf(state, offer.itemId)
    if (!origin) return null
    const mine = openMissionOf(state, operator.employeeId)
    return {
      offerId: offer.offerId,
      taskId: offer.taskId,
      taskVersion: offer.taskVersion,
      status: offer.status,
      isRetry: false,
      operationId: offer.operationId,
      routeId: origin.mission.header.routeId,
      routeName: origin.mission.header.routeName,
      routeColor: origin.mission.header.routeColor,
      snapshot: origin.item.snapshot,
      taskStatus: origin.item.status === 'PROBLEM' ? 'PROBLEM' : origin.item.status === 'COMPLETED' ? 'DONE' : 'TODO',
      createdAt: offer.createdAt,
      claimedAt: offer.claimedAt,
      claimedByName: offer.claimedByName,
      claimedByMe: offer.status === 'CLAIMED' ? offer.claimedByEmployeeId === operator.employeeId : null,
      canClaim:
        offer.status === 'ACTIVE' &&
        mine?.header.status === 'IN_PROGRESS' &&
        mine.header.operationId === offer.operationId &&
        origin.mission.header.id !== mine.header.id,
    }
  }

  /** Prise en charge : la résidence d'origine est marquée transférée, une résidence est ajoutée à la mission. */
  function transfer(state: ServerState, offer: FixtureOffer, to: StoredMission, operatorName: string): FieldMissionItem {
    const origin = ownerOf(state, offer.itemId)
    if (!origin) return fail('OFFER_NOT_FOUND')
    const at = now()
    origin.item.transfer = { at, toMissionId: to.header.id, operatorName }
    origin.item.itemVersion += 1
    const item: FieldMissionItem = {
      ...origin.item,
      id: crypto.randomUUID(),
      missionId: to.header.id,
      order: to.items.length + 1,
      itemVersion: 1,
      events: [],
      transfer: null,
    }
    to.items.push(item)
    offer.status = 'CLAIMED'
    offer.claimedAt = at
    offer.claimedByName = operatorName
    offer.taskVersion += 1
    return item
  }

  function conflictResult(mission: StoredMission, item: FieldMissionItem, kind: 'COMPLETE_AFTER_TRANSFER' | 'PROBLEM_AFTER_TRANSFER') {
    return {
      itemId: item.id,
      status: item.status,
      alreadyCompleted: false,
      itemVersion: item.itemVersion,
      progress: progress(mission.items),
      conflict: {
        kind,
        ownerName: item.transfer?.operatorName ?? null,
        ownerMissionId: item.transfer?.toMissionId ?? null,
      },
    }
  }

  function ownMission(state: ServerState, operator: Operator, missionId: string): StoredMission {
    const mission = state.missions.find(
      (candidate) => candidate.header.id === missionId && candidate.header.operatorId === operator.employeeId,
    )
    if (!mission) fail('MISSION_NOT_FOUND')
    return mission
  }

  function lastReading(state: ServerState, equipmentId: string): HourReading | null {
    const list = state.readings[equipmentId] ?? []
    return list.at(-1) ?? null
  }

  function itemFor(state: ServerState, operator: Operator, itemId: string) {
    for (const mission of state.missions) {
      const item = mission.items.find((candidate) => candidate.id === itemId)
      if (item) {
        if (mission.header.operatorId !== operator.employeeId) fail('MISSION_ITEM_NOT_FOUND')
        return { mission, item }
      }
    }
    return fail('MISSION_ITEM_NOT_FOUND')
  }

  function assertInProgress(mission: StoredMission) {
    if (mission.header.status === 'PAUSED') fail('MISSION_PAUSED')
    if (mission.header.status !== 'IN_PROGRESS') fail('MISSION_NOT_IN_PROGRESS')
  }

  function event(item: FieldMissionItem, kind: FieldItemEvent['kind'], to: FieldItemEvent['toStatus'], extra: Partial<FieldItemEvent>, actorName: string) {
    item.events.push({
      id: crypto.randomUUID(),
      kind,
      fromStatus: item.status,
      toStatus: to,
      reasonCode: null,
      reasonText: null,
      note: null,
      occurredAt: now(),
      actorName,
      ...extra,
    })
  }

  return {
    home() {
      return run((state) => {
        const { operator } = actor()
        const openMission = state.missions.find((mission) => mission.header.operatorId === operator.employeeId && open(mission))
        const blockers: FieldStartBlocker[] = []
        const route = operator.routeId ? routes[operator.routeId] : undefined
        const equipment = operator.equipmentId ? equipments[operator.equipmentId] : undefined
        if (!operator.routeId) blockers.push('ROUTE_MISSING')
        else if (!openMission && state.missions.some((mission) => mission.header.routeId === operator.routeId && occupiesRoute(mission)))
          blockers.push('ROUTE_IN_USE')
        if (!operator.equipmentId) blockers.push('EQUIPMENT_MISSING')
        else if (!openMission && state.missions.some((mission) => mission.header.equipmentId === operator.equipmentId && open(mission)))
          blockers.push('EQUIPMENT_IN_USE')
        const home: OperatorMissionHome = {
          operator: { employeeId: operator.employeeId, name: operator.name },
          route:
            operator.routeId && route
              ? { id: operator.routeId, name: route.name, color: route.color, season: '2026-2027', available: true, eligibleCount: residences.length }
              : null,
          equipment:
            operator.equipmentId && equipment
              ? {
                  id: operator.equipmentId,
                  number: equipment.number,
                  name: equipment.name,
                  available: true,
                  lastReading: lastReading(state, operator.equipmentId),
                }
              : null,
          openMission: openMission ? header(openMission) : null,
          blockers: openMission ? [] : blockers,
          operation: (() => {
            const operation = openOperation(state)
            if (!operation) return null
            const ageHours = Math.max(0, (Date.now() - Date.parse(operation.openedAt)) / 3_600_000)
            return {
              id: operation.id,
              number: operation.number,
              status: operation.status,
              openedAt: operation.openedAt,
              ageHours: Math.round(ageHours * 10) / 10,
              stale: ageHours > 24,
            }
          })(),
        }
        return home
      })
    },
    routePlan() {
      return run((state) => {
        const { operator } = actor()
        const operation = openOperation(state)
        const planRoutes = operator.routeIds.map((routeId, index) => {
          const route = routes[routeId]
          return {
            rank: index + 1,
            routeId,
            name: route?.name ?? null,
            color: route?.color ?? null,
            description: null,
            available: true,
            eligibleNow: residences.length,
            ...routeState(state, operation?.id ?? null, routeId),
          }
        })
        const plan: OperatorRoutePlan = {
          operation: operation
            ? {
                id: operation.id,
                number: operation.number,
                status: operation.status,
                openedAt: operation.openedAt,
                ageHours: 0,
                stale: false,
              }
            : null,
          routes: planRoutes,
          nextRouteId:
            planRoutes.find((route) => route.available && ['START', 'RESUME', 'NEW_PASS'].includes(route.action))
              ?.routeId ?? null,
        }
        return plan
      })
    },
    start({ idempotencyKey, startEngineHours, routeId: chosenRouteId, expectedPassNumber }) {
      return run((state) => {
        const { userId, operator } = actor()
        return claim(state, userId, idempotencyKey, 'START', null, () => {
          if (state.missions.some((mission) => mission.header.operatorId === operator.employeeId && open(mission)))
            fail('MISSION_OPERATOR_ALREADY_OPEN')
          if (chosenRouteId !== undefined && !operator.routeIds.includes(chosenRouteId)) fail('MISSION_ROUTE_NOT_ASSIGNED')
          const routeId = chosenRouteId ?? operator.routeId
          if (!routeId) fail('MISSION_ROUTE_MISSING')
          if (!operator.equipmentId) fail('MISSION_EQUIPMENT_MISSING')
          const equipmentId = operator.equipmentId
          if (state.missions.some((mission) => mission.header.equipmentId === equipmentId && open(mission)))
            fail('MISSION_EQUIPMENT_IN_USE')
          if (state.missions.some((mission) => mission.header.routeId === routeId && occupiesRoute(mission)))
            fail('MISSION_ROUTE_IN_USE')
          const hours = Number(startEngineHours)
          const last = lastReading(state, equipmentId)
          if (last && hours < last.value) fail('MISSION_ENGINE_HOURS_BELOW_LAST')
          // Passage décidé par le serveur : passage préparé engagé, sinon reprise du passage en cours.
          const before = routeState(state, openOperation(state)?.id ?? null, routeId)
          if (before.action === 'COMPLETED') fail('MISSION_ROUTE_COMPLETED_IN_OPERATION')
          if (expectedPassNumber !== undefined && expectedPassNumber !== before.passNumber)
            fail('MISSION_PASS_CHANGED', String(before.passNumber))
          const route = routes[routeId]
          const equipment = equipments[equipmentId]
          const missionId = crypto.randomUUID()
          state.sequence += 1
          const startedAt = now()
          const operation = attachOperation(state, missionId)
          const passNumber = before.passNumber
          const prepared = state.passes.find((pass) => pass.id === before.preparedPassId)
          if (prepared) prepared.status = 'STARTED'
          else if (before.action === 'START')
            state.passes.push({ id: crypto.randomUUID(), operationId: operation.id, routeId, passNumber, status: 'STARTED' })
          // Reprise : seulement les résidences pas encore réglées dans ce passage.
          const settled = new Set(
            before.action === 'RESUME'
              ? state.missions
                  .filter(
                    (mission) =>
                      mission.header.operationId === operation.id &&
                      mission.header.routeId === routeId &&
                      (mission.passNumber ?? 1) === passNumber,
                  )
                  .flatMap((mission) => mission.items)
                  .filter((item) => item.status === 'COMPLETED')
                  .map((item) => item.serviceLocationId)
              : [],
          )
          state.missions.push({
            header: {
              id: missionId,
              number: state.sequence,
              status: 'IN_PROGRESS',
              legacyStatus: 'en_cours',
              organizationId: null,
              season: '2026-2027',
              routeId,
              routeName: route?.name ?? null,
              routeColor: route?.color ?? null,
              routeVersion: route?.version ?? null,
              routeDescription: 'Commencer par la rue Labelle, côté pair.',
              operatorId: operator.employeeId,
              operatorName: operator.name,
              equipmentId,
              equipmentLabel: equipment ? `${equipment.number} — ${equipment.name}` : null,
              startedAt,
              endedAt: null,
              pausedAt: null,
              startEngineHours: hours,
              endEngineHours: null,
              endSummary: null,
              cancelledReason: null,
              detectionProfile: fixtureDetectionProfile,
              operationId: operation.id,
              operationNumber: operation.number,
              missionKind: 'NORMAL',
              cancelKind: null,
              routeGuide: fixtureRouteGuide(
                residences.map((residence, index) => {
                  const flag = fixtureFlag(residence.lat, residence.lng)
                  return {
                    serviceLocationId: `e0000000-0000-4000-a000-${String(index + 1).padStart(12, '0')}`,
                    order: index + 1,
                    service: residence.lat === null ? null : [residence.lat, residence.lng],
                    flag: flag ? [flag.latitude, flag.longitude] : null,
                  }
                }),
                {
                  withPath: routeId === 'c0000000-0000-4000-a000-000000000001',
                  nextRoute: nextRouteOf(operator, routeId),
                  now: new Date(startedAt),
                },
              ),
            },
            items: residences
              .map((residence, index) => ({
              id: crypto.randomUUID(),
              operationTaskId: crypto.randomUUID(),
              transfer: null,
              missionId,
              order: index + 1,
              status: 'WAITING' as const,
              itemVersion: 1,
              serviceLocationId: `e0000000-0000-4000-a000-${String(index + 1).padStart(12, '0')}`,
              contractId: `f0000000-0000-4000-a000-${String(index + 1).padStart(12, '0')}`,
              snapshot: {
                addressLine1: residence.line1,
                addressLine2: residence.unit,
                city: 'Saint-Jérôme',
                postalCode: 'J7Z 1A1',
                formattedAddress: null,
                clientName: residence.client,
                contractNumber: `CTR-26${String(1000 + index)}`,
                latitude: residence.lat,
                longitude: residence.lng,
                routeOrder: index + 1,
                flag: fixtureFlag(residence.lat, residence.lng),
              },
              completedAt: null,
              clearedAt: null,
              clearedReason: null,
              events: [],
              phone: residence.phone,
              locationInstructions: index === 0 ? 'Entrée partagée : dégager les deux côtés.' : null,
              operatorMessage: index === 2 ? 'Attention au muret à gauche de l’entrée.' : null,
              specialInstructions: null,
              knownObstacles: index === 4 ? 'Bac à fleurs en bordure.' : null,
              }))
              .filter((item) => !settled.has(item.serviceLocationId))
              .map((item, index) => ({ ...item, order: index + 1 })),
            pauses: [],
            passNumber,
          })
          ;(state.readings[equipmentId] ??= []).push({ value: hours, kind: 'START', recordedAt: startedAt, missionId })
          return {
            missionId,
            number: state.sequence,
            status: 'IN_PROGRESS' as const,
            items: residences.length - settled.size,
            operationId: operation.id,
            operationNumber: operation.number,
            passNumber,
            routeId,
          }
        })
      })
    },
    view(missionId) {
      return run((state) => {
        const { operator } = actor()
        const mission = ownMission(state, operator, missionId)
        return { mission: header(mission), items: mission.items }
      })
    },
    complete({ idempotencyKey, itemId }) {
      return run((state) => {
        const { userId, operator } = actor()
        const { mission, item } = itemFor(state, operator, itemId)
        return claim(state, userId, idempotencyKey, 'COMPLETE_ITEM', itemId, () => {
          assertInProgress(mission)
          if (item.transfer) return conflictResult(mission, item, 'COMPLETE_AFTER_TRANSFER')
          if (item.status === 'COMPLETED')
            return { itemId, status: 'COMPLETED' as const, alreadyCompleted: true, itemVersion: item.itemVersion, progress: progress(mission.items) }
          event(item, item.status === 'PROBLEM' ? 'PROBLEM_RESOLVED' : 'COMPLETED', 'COMPLETED', {}, operator.name)
          item.status = 'COMPLETED'
          item.completedAt = now()
          item.itemVersion += 1
          return { itemId, status: 'COMPLETED' as const, alreadyCompleted: false, itemVersion: item.itemVersion, progress: progress(mission.items) }
        })
      })
    },
    reportProblem({ idempotencyKey, itemId, reasonCode, reasonText, note }) {
      return run((state) => {
        const { userId, operator } = actor()
        if (reasonCode === 'OTHER' && !reasonText?.trim()) fail('MISSION_PROBLEM_TEXT_REQUIRED')
        const { mission, item } = itemFor(state, operator, itemId)
        return claim(state, userId, idempotencyKey, 'REPORT_PROBLEM', itemId, () => {
          assertInProgress(mission)
          if (item.transfer) return conflictResult(mission, item, 'PROBLEM_AFTER_TRANSFER')
          if (item.status === 'COMPLETED') fail('MISSION_ITEM_ALREADY_COMPLETED')
          event(
            item,
            'PROBLEM_REPORTED',
            'PROBLEM',
            { reasonCode, reasonText: reasonText?.trim() ?? null, note: note?.trim() ?? null },
            operator.name,
          )
          item.status = 'PROBLEM'
          item.itemVersion += 1
          return { itemId, status: 'PROBLEM' as const, itemVersion: item.itemVersion, progress: progress(mission.items) }
        })
      })
    },
    pause({ idempotencyKey, missionId }) {
      return run((state) => {
        const { userId, operator } = actor()
        const mission = ownMission(state, operator, missionId)
        return claim(state, userId, idempotencyKey, 'PAUSE', missionId, () => {
          if (mission.header.status === 'PAUSED')
            return { missionId, status: 'PAUSED' as const, pausedAt: mission.header.pausedAt }
          if (mission.header.status !== 'IN_PROGRESS') fail('MISSION_NOT_IN_PROGRESS')
          const at = now()
          mission.header.status = 'PAUSED'
          mission.header.pausedAt = at
          mission.pauses.push({ startedAt: at, endedAt: null })
          return { missionId, status: 'PAUSED' as const, pausedAt: at }
        })
      })
    },
    resume({ idempotencyKey, missionId }) {
      return run((state) => {
        const { userId, operator } = actor()
        const mission = ownMission(state, operator, missionId)
        return claim(state, userId, idempotencyKey, 'RESUME', missionId, () => {
          if (mission.header.status === 'IN_PROGRESS') return { missionId, status: 'IN_PROGRESS' as const }
          if (mission.header.status !== 'PAUSED') fail('MISSION_NOT_PAUSED')
          mission.header.status = 'IN_PROGRESS'
          mission.header.pausedAt = null
          const pause = mission.pauses.find((candidate) => candidate.endedAt === null)
          if (pause) pause.endedAt = now()
          return { missionId, status: 'IN_PROGRESS' as const }
        })
      })
    },
    end({ idempotencyKey, missionId, endEngineHours }) {
      return run((state) => {
        const { userId, operator } = actor()
        const mission = ownMission(state, operator, missionId)
        return claim(state, userId, idempotencyKey, 'END', missionId, () => {
          if (!open(mission)) fail('MISSION_NOT_OPEN')
          const hours = Number(endEngineHours)
          if (hours < mission.header.startEngineHours) fail('MISSION_ENGINE_HOURS_BELOW_START')
          const at = now()
          for (const pause of mission.pauses) pause.endedAt ??= at
          const summary = progress(mission.items)
          const unresolved = summary.waiting + summary.problem > 0
          mission.header.status = 'COMPLETED'
          mission.header.legacyStatus = unresolved ? 'terminee_avec_anomalies' : 'terminee'
          mission.header.endedAt = at
          mission.header.pausedAt = null
          mission.header.endEngineHours = hours
          mission.header.endSummary = { ...summary, unresolved }
          if (mission.header.equipmentId)
            (state.readings[mission.header.equipmentId] ??= []).push({ value: hours, kind: 'END', recordedAt: at, missionId })
          return { mission: header(mission) }
        })
      })
    },
    sendPositions(missionId, points) {
      return run((state) => {
        const { operator } = actor()
        const mission = ownMission(state, operator, missionId)
        const acknowledged: string[] = []
        const rejected: { id: string; reason: 'FUTURE' | 'BEFORE_START' | 'AFTER_END' | 'DURING_PAUSE' | 'ID_CONFLICT' }[] = []
        const receivedAt = now()
        for (const point of points) {
          const existing = state.positions[point.id]
          if (existing) {
            if (existing.missionId === missionId) acknowledged.push(point.id)
            else rejected.push({ id: point.id, reason: 'ID_CONFLICT' })
            continue
          }
          const captured = Date.parse(point.capturedAt)
          const reason =
            captured > Date.now() + 120_000
              ? 'FUTURE'
              : captured < Date.parse(mission.header.startedAt) - 60_000
                ? 'BEFORE_START'
                : mission.header.endedAt && captured > Date.parse(mission.header.endedAt)
                  ? 'AFTER_END'
                  : mission.pauses.some(
                        (pause) =>
                          captured >= Date.parse(pause.startedAt) &&
                          (pause.endedAt === null || captured < Date.parse(pause.endedAt)),
                      )
                    ? 'DURING_PAUSE'
                    : null
          if (reason) {
            rejected.push({ id: point.id, reason })
            continue
          }
          state.positions[point.id] = {
            missionId,
            capturedAt: point.capturedAt,
            latitude: point.latitude,
            longitude: point.longitude,
            accuracy: point.accuracy,
            receivedAt,
          }
          acknowledged.push(point.id)
        }
        return { acknowledged, rejected, receivedAt }
      })
    },
    sendGap(gap) {
      return run((state) => {
        const { operator } = actor()
        ownMission(state, operator, gap.missionId)
        const existing = state.gaps.find((candidate) => candidate.id === gap.id)
        if (existing) existing.endedAt ??= gap.endedAt
        else state.gaps.push(gap)
      })
    },
    track(missionId) {
      return run((state) => {
        const { operator } = actor()
        const mission = ownMission(state, operator, missionId)
        return {
          missionId,
          points: Object.entries(state.positions)
            .filter(([, point]) => point.missionId === missionId)
            .map(([id, point]) => ({ id, ...point }))
            .sort((a, b) => a.capturedAt.localeCompare(b.capturedAt)),
          pauses: mission.pauses,
          gaps: state.gaps
            .filter((gap) => gap.missionId === missionId)
            .map((gap) => ({ kind: gap.kind, startedAt: gap.startedAt, endedAt: gap.endedAt })),
        }
      })
    },
    cancelStart({ idempotencyKey, missionId }) {
      return run((state) => {
        const { userId, operator } = actor()
        const mission = ownMission(state, operator, missionId)
        return claim(state, userId, idempotencyKey, 'CANCEL_START', missionId, () => {
          if (!open(mission)) fail('MISSION_NOT_OPEN')
          // Le serveur décide : un événement de résidence suffit pour exiger le bureau.
          if (mission.items.some((item) => item.events.length > 0)) fail('MISSION_HAS_WORK')
          const at = now()
          for (const pause of mission.pauses) pause.endedAt ??= at
          mission.header.status = 'CANCELLED'
          mission.header.legacyStatus = 'annulee'
          mission.header.endedAt = at
          mission.header.pausedAt = null
          mission.header.cancelledReason = 'Démarrage accidentel'
          mission.header.cancelKind = 'ACCIDENTAL_START'
          mission.header.endSummary = { ...progress(mission.items), unresolved: false }
          const operation = state.operations.find((candidate) => candidate.id === mission.header.operationId)
          const others = state.missions.filter(
            (candidate) =>
              candidate.header.operationId === operation?.id &&
              candidate.header.id !== mission.header.id &&
              candidate.header.status !== 'CANCELLED',
          )
          const operationCancelled = operation?.openedByMissionId === mission.header.id && others.length === 0
          if (operation && operationCancelled) operation.status = 'CANCELLED'
          return {
            mission: header(mission),
            withdrawnTasks: mission.items.length,
            tasksToReassign: 0,
            operationCancelled,
          }
        })
      })
    },
    heartbeat({ sessionId }) {
      return run((state) => {
        const { operator } = actor()
        const receivedAt = now()
        const previous = state.presence[operator.employeeId]
        state.presence[operator.employeeId] = {
          sessionId,
          connected: true,
          lastBeatAt: receivedAt,
          availableForHelp: previous?.availableForHelp ?? false,
        }
        return {
          receivedAt,
          connection: 'CONNECTED' as const,
          positionAccepted: false,
          availableForHelp: previous?.availableForHelp ?? false,
        }
      })
    },
    disconnect() {
      return run((state) => {
        const { operator } = actor()
        const presence = state.presence[operator.employeeId]
        if (presence) {
          presence.connected = false
          presence.availableForHelp = false
        }
      })
    },
    setHelpAvailability(available) {
      return run((state) => {
        const { operator } = actor()
        if (available && openMissionOf(state, operator.employeeId)) fail('OPERATOR_IN_MISSION')
        const presence = state.presence[operator.employeeId]
        if (!presence?.connected) fail('PRESENCE_NOT_CONNECTED')
        presence.availableForHelp = available
        return { availableForHelp: available }
      })
    },
    sendHelpRequest({ id, reason, comment }) {
      return run((state) => {
        const { userId, operator } = actor()
        const existing = state.help.find((request) => request.id === id)
        if (existing) {
          if (existing.userId !== userId) fail('HELP_REQUEST_ID_CONFLICT')
          return { id, status: existing.status, createdAt: existing.createdAt, replayed: true }
        }
        const createdAt = now()
        state.help.push({
          id,
          employeeId: operator.employeeId,
          userId,
          reason,
          comment: comment?.trim() ? comment.trim() : null,
          status: 'SENT',
          createdAt,
          acknowledgedAt: null,
          acknowledgedByName: null,
          resolvedAt: null,
        })
        return { id, status: 'SENT' as const, createdAt, replayed: false }
      })
    },
    helpRequests() {
      return run((state) => {
        const { operator } = actor()
        return state.help
          .filter((request) => request.employeeId === operator.employeeId)
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
          .map(({ id, reason, comment, status, createdAt, acknowledgedAt, acknowledgedByName, resolvedAt }) => ({
            id,
            reason,
            comment,
            status,
            createdAt,
            acknowledgedAt,
            acknowledgedByName,
            resolvedAt,
          }))
      })
    },
    reinforcementOffers() {
      return run((state) => {
        const { operator } = actor()
        const mission = openMissionOf(state, operator.employeeId)
        const operation = openOperation(state)
        return {
          missionId: mission?.header.id ?? null,
          missionStatus: mission?.header.status ?? null,
          operationId: mission?.header.operationId ?? null,
          offers: state.offers
            .filter((offer) => offer.operationId === operation?.id && offer.status !== 'REVOKED')
            .flatMap((offer) => {
              const view = offerView(state, offer, operator)
              return view ? [view] : []
            }),
        }
      })
    },
    claimTask({ idempotencyKey, offerId, expectedVersion }) {
      return run((state) => {
        const { userId, operator } = actor()
        const mission = openMissionOf(state, operator.employeeId)
        return claim(state, userId, idempotencyKey, 'CLAIM_TASK', offerId, () => {
          if (!mission) fail('REINFORCEMENT_MISSION_REQUIRED')
          assertInProgress(mission)
          if (!state.presence[operator.employeeId]?.connected) fail('PRESENCE_NOT_CONNECTED')
          const offer = state.offers.find((candidate) => candidate.offerId === offerId)
          if (!offer) fail('OFFER_NOT_FOUND')
          if (offer.status === 'CLAIMED') fail('TASK_ALREADY_CLAIMED', offer.claimedByName ?? '')
          if (offer.status === 'REVOKED') fail('OFFER_REVOKED')
          if (mission.header.operationId !== offer.operationId) fail('MISSION_NOT_IN_OPERATION')
          if (offer.taskVersion !== expectedVersion) fail('OPERATION_TASK_VERSION_CONFLICT', String(offer.taskVersion))
          offer.claimedByEmployeeId = operator.employeeId
          const item = transfer(state, offer, mission, operator.name)
          return {
            offerId,
            status: 'CLAIMED' as const,
            taskId: offer.taskId,
            taskVersion: offer.taskVersion,
            itemId: item.id,
            missionId: mission.header.id,
            ownerName: operator.name,
          }
        })
      })
    },
    startReinforcement({ idempotencyKey, startEngineHours }) {
      return run((state) => {
        const { userId, operator } = actor()
        return claim(state, userId, idempotencyKey, 'START_REINFORCEMENT', null, () => {
          if (openMissionOf(state, operator.employeeId)) fail('MISSION_OPERATOR_ALREADY_OPEN')
          if (!operator.routeId) fail('MISSION_ROUTE_MISSING')
          if (!operator.equipmentId) fail('MISSION_EQUIPMENT_MISSING')
          const equipmentId = operator.equipmentId
          if (state.missions.some((mission) => mission.header.equipmentId === equipmentId && open(mission)))
            fail('MISSION_EQUIPMENT_IN_USE')
          const hours = Number(startEngineHours)
          const last = lastReading(state, equipmentId)
          if (last && hours < last.value) fail('MISSION_ENGINE_HOURS_BELOW_LAST', String(last.value))
          const operation = openOperation(state)
          if (!operation) fail('OPERATION_NOT_OPEN')
          const equipment = equipments[equipmentId]
          const missionId = crypto.randomUUID()
          state.sequence += 1
          const startedAt = now()
          state.missions.push({
            header: {
              id: missionId,
              number: state.sequence,
              status: 'IN_PROGRESS',
              legacyStatus: 'en_cours',
              organizationId: null,
              season: '2026-2027',
              routeId: operator.routeId,
              routeName: 'Renfort',
              routeColor: null,
              routeVersion: null,
              routeDescription: null,
              operatorId: operator.employeeId,
              operatorName: operator.name,
              equipmentId,
              equipmentLabel: equipment ? `${equipment.number} — ${equipment.name}` : null,
              startedAt,
              endedAt: null,
              pausedAt: null,
              startEngineHours: hours,
              endEngineHours: null,
              endSummary: null,
              cancelledReason: null,
              detectionProfile: fixtureDetectionProfile,
              operationId: operation.id,
              operationNumber: operation.number,
              missionKind: 'REINFORCEMENT',
              cancelKind: null,
            },
            items: [],
            pauses: [],
          })
          ;(state.readings[equipmentId] ??= []).push({ value: hours, kind: 'START', recordedAt: startedAt, missionId })
          const presence = state.presence[operator.employeeId]
          if (presence) presence.availableForHelp = false
          return {
            missionId,
            number: state.sequence,
            status: 'IN_PROGRESS' as const,
            items: 0 as const,
            operationId: operation.id,
            operationNumber: operation.number,
            missionKind: 'REINFORCEMENT' as const,
          }
        })
      })
    },
  }
}
